import { Editor, EditorContent } from '@tiptap/vue-3';
import { BubbleMenu } from '@tiptap/vue-3/menus';
import Bold from '@tiptap/extension-bold';
import Blockquote from '@tiptap/extension-blockquote';
import BubbleMenuExtension from '@tiptap/extension-bubble-menu';
import { BulletList, OrderedList, ListItem } from '@tiptap/extension-list';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import { Placeholder, UndoRedo, Dropcursor, Gapcursor } from '@tiptap/extensions';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Image from '@tiptap/extension-image';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import Paragraph from '@tiptap/extension-paragraph';
import Strike from '@tiptap/extension-strike';
import Text from '@tiptap/extension-text';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';

// Custom Extensions
import CustomSellsyAI from 'vcomponents/richtext/customextensions/CustomSellsyAI';
import CustomTextSize from 'vcomponents/richtext/customextensions/CustomTextSize';
import CustomSuggestion from 'vcomponents/richtext/customextensions/CustomSuggestion';
import ImageResizable from 'vcomponents/richtext/customextensions/CustomImageResizable.js';

import { normalizeKeyName } from '../helpers/normalizeKeyHelper';
import SessionStorage from 'Core/commons/SessionStorage';
import SellsyAIInput from 'vcomponents/richtext/customextensions/SellsyAIInput';
import Feature from 'Services/Feature';
import CustomTagsAndFields from '../components/richtext/customextensions/CustomTagsAndFields';
import CustomTagsAI from '../components/richtext/customextensions/CustomTagsAI';
import CustomSignature from '../components/richtext/customextensions/CustomSignature';

const defaultColorsGrid = [
    ['#000000', '#404040', '#737373', '#A3A3A3', '#D4D4D4', '#FFFFFF'],
    ['#41086E', '#540303', '#693300', '#4D3C00', '#06371A', '#19295C'],
    ['#6B21A8', '#9A0000', '#AB3E00', '#916200', '#166534', '#1E40AF'],
    ['#9333EA', '#DC2626', '#EA580C', '#CA8A04', '#16A34A', '#2563EB'],
    ['#C084FC', '#F87171', '#FB923C', '#FACC15', '#4ADE80', '#60A5FA'],
    ['#E9D5FF', '#FECACA', '#FED7AA', '#FEF08A', '#BBF7D0', '#BFDBFE'],
];

export default {
    components: {
        EditorContent,
        BubbleMenu,
    },
    props: {
        placeholder: {
            type: String,
            default: '',
        },
        options: {
            type: Array,
            default: () => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        useStorage: {
            type: Boolean,
            default: false,
        },
        disableEnter: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            // Editor instance
            editor: null,
            // localStorage
            storage: null,
            // Global options for the editor
            // --- extension: import from tiptap
            // --- action: function to execute
            // --- label: the name of the button and the tooltip
            // --- icon: the name of the icon to display
            // --- shortcuts: the keyboard shortcut to use
            extensionsMap: {
                SellsyAI: {
                    extension: CustomSellsyAI,
                    label: 'richtext.sellsy_ai',
                },
                SellsyAIInput: {
                    extension: SellsyAIInput,
                    label: 'SellsyAIInput',
                },
                Attachment: {
                    label: 'richtext.attachment',
                    icon: 'attachment',
                },
                Blockquote: {
                    extension: Blockquote,
                    action: () => this.editor.chain().focus().toggleBlockquote().run(),
                    label: 'richtext.citation',
                    icon: 'richeditor-quote-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + B',
                },
                BlockquoteTiptap: {
                    // Use Blockquote extension from TIPTAP without add option on menu
                    extension: Blockquote,
                },
                Bold: {
                    extension: Bold,
                    action: () => this.editor.chain().focus().toggleBold().run(),
                    label: 'richtext.bold',
                    icon: 'richeditor-bold-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + B',
                },
                BubbleMenuAI: {
                    extension: BubbleMenuExtension.configure({
                        pluginKey: 'bubbleMenuAI',
                    }),
                },
                BulletList: {
                    extension: BulletList,
                    action: () => this.editor.chain().focus().toggleBulletList().run(),
                    label: 'richtext.bullet_list',
                    icon: 'richeditor-bulletlist-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + 8',
                },
                Code: {
                    extension: Code,
                    action: () => this.editor.chain().focus().toggleCode().run(),
                    label: 'richtext.code',
                    icon: 'richeditor-code',
                    shortcuts: normalizeKeyName('Ctrl') + ' + E',
                },
                CodeBlock: {
                    extension: CodeBlock,
                    action: () => this.editor.chain().focus().toggleCodeBlock().run(),
                    label: 'richtext.code_block',
                    icon: 'richeditor-code',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + C',
                },
                Color: {
                    extension: Color.configure({
                        types: ['textStyle'],
                    }),
                    label: 'richtext.text_color',
                    icon: 'richeditor-text-color',
                    colorsGrid: defaultColorsGrid,
                },
                CustomTags: {
                    label: 'richtext.label',
                    icon: 'nav-cashregister',
                },
                CustomTagsAndFields: {
                    extension: CustomTagsAndFields,
                },
                CustomTagsAI: {
                    extension: CustomTagsAI,
                },
                Heading: {
                    extension: Heading,
                    label: 'richtext.format',
                },
                HeadingTiptap: {
                    // Use Heading extension from TIPTAP without add option on menu
                    extension: Heading,
                },
                Heading1: {
                    extension: Heading.configure({
                        levels: [1],
                    }),
                    action: () => this.editor.chain().focus().toggleHeading({ level: 1 }).run(),
                    label: 'richtext.title_1',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + 1',
                },
                Heading2: {
                    extension: Heading.configure({
                        levels: [2],
                    }),
                    action: () => this.editor.chain().focus().toggleHeading({ level: 2 }).run(),
                    label: 'richtext.title_2',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + 2',
                },
                Heading3: {
                    extension: Heading.configure({
                        levels: [3],
                    }),
                    action: () => this.editor.chain().focus().toggleHeading({ level: 3 }).run(),
                    label: 'richtext.title_3',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + 3',
                },
                Heading4: {
                    extension: Heading.configure({
                        levels: [4],
                    }),
                    action: () => this.editor.chain().focus().toggleHeading({ level: 4 }).run(),
                    label: 'richtext.title_4',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + 4',
                },
                Heading5: {
                    extension: Heading.configure({
                        levels: [5],
                    }),
                    action: () => this.editor.chain().focus().toggleHeading({ level: 5 }).run(),
                    label: 'richtext.title_5',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + 5',
                },
                Heading6: {
                    extension: Heading.configure({
                        levels: [6],
                    }),
                    action: () => this.editor.chain().focus().toggleHeading({ level: 6 }).run(),
                    label: 'richtext.title_6',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + 6',
                },
                Highlight: {
                    extension: Highlight.configure({
                        multicolor: true,
                    }),
                    label: 'richtext.text_highlight',
                    icon: 'richeditor-text-highlight',
                    colorsGrid: defaultColorsGrid,
                },
                Image: {
                    extension: Image.configure({
                        inline: true,
                        HTMLAttributes: {
                            style: 'max-width: 100%; height: auto;',
                        },
                    }),
                    label: 'richtext.image',
                    icon: 'richeditor-image',
                },
                ImageResizable: {
                    extension: ImageResizable,
                    label: 'richtext.image',
                    icon: 'richeditor-image',
                },
                IndentIncrease: {
                    extension: ListItem,
                    action: () => this.editor.chain().focus().indentList().run(),
                    label: 'richtext.indent_increase',
                    icon: 'richeditor-indent-increase',
                    shortcuts: 'Tab',
                },
                Italic: {
                    extension: Italic,
                    action: () => this.editor.chain().focus().toggleItalic().run(),
                    label: 'richtext.italic',
                    icon: 'richeditor-italic-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + I',
                },
                Link: {
                    extension: Link.configure({
                        validate: (url) => /^https?:\/\//.test(url),
                        openOnClick: true,
                    }),
                    // specific action (setLinkUrl / unLinkUrl methods)
                    label: 'richtext.link',
                    icon: 'richeditor-link',
                },
                OrderedList: {
                    extension: OrderedList,
                    action: () => this.editor.chain().focus().toggleOrderedList().run(),
                    label: 'richtext.number_list',
                    icon: 'richeditor-numberlist-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + 7',
                },
                Paragraph: {
                    extension: Paragraph,
                    action: () => this.editor.chain().focus().setParagraph().run(),
                    label: 'richtext.paragraph',
                    // no icon
                    shortcuts: normalizeKeyName('Ctrl') + ' + Alt + 0',
                },
                Size: {
                    extension: CustomTextSize,
                    action: (size) => this.editor.chain().focus().setFontSize(size).run(),
                    label: 'richtext.text_height',
                    icon: 'richeditor-text-height',
                },
                SizeTiptap: {
                    // Use CustomTextSize extension from TIPTAP without add option on menu
                    extension: CustomTextSize,
                },
                UnSize: {
                    extension: CustomTextSize,
                    action: () => this.editor.chain().focus().unsetFontSize().run(),
                    label: 'richtext.default_height',
                    icon: 'richeditor-text-height',
                },
                Strike: {
                    extension: Strike,
                    action: () => this.editor.chain().focus().toggleStrike().run(),
                    label: 'richtext.strike',
                    icon: 'richeditor-strikethrough',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + X',
                },
                Table: {
                    extension: Table,
                    label: 'richtext.table',
                    icon: 'richeditor-table',
                },
                TableCell: {
                    extension: TableCell,
                },
                TableHeader: {
                    extension: TableHeader,
                },
                TableRow: {
                    extension: TableRow,
                },
                TextAlign: {
                    extension: TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                    label: 'richtext.align',
                    icon: 'richeditor-leftalign-text',
                },
                TextAlignLeft: {
                    extension: TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                    action: () => this.editor.chain().focus().setTextAlign('left').run(),
                    label: 'richtext.align_left',
                    icon: 'richeditor-leftalign-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + L',
                },
                TextAlignCenter: {
                    extension: TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                    action: () => this.editor.chain().focus().setTextAlign('center').run(),
                    label: 'richtext.align_center',
                    icon: 'richeditor-centrealign-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + E',
                },
                TextAlignRight: {
                    extension: TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                    action: () => this.editor.chain().focus().setTextAlign('right').run(),
                    label: 'richtext.align_right',
                    icon: 'richeditor-rightalign-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + R',
                },
                TextAlignJustify: {
                    extension: TextAlign.configure({
                        types: ['heading', 'paragraph'],
                    }),
                    action: () => this.editor.chain().focus().setTextAlign('justify').run(),
                    label: 'richtext.align_justify',
                    icon: 'richeditor-justifyalign-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + Shift + J',
                },
                Separator: {
                    extension: HorizontalRule,
                    action: () => this.editor.chain().focus().setHorizontalRule().run(),
                    label: 'richtext.divider',
                    icon: 'richeditor-divider',
                },
                Underline: {
                    extension: Underline,
                    action: () => this.editor.chain().focus().toggleUnderline().run(),
                    label: 'richtext.underline',
                    icon: 'richeditor-underline-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + U',
                },
                Unformat: {
                    extension: TextStyle.configure({
                        types: ['textStyle'],
                    }),
                    action: () => this.editor.chain().focus().unsetAllMarks().clearNodes().run(),
                    label: 'richtext.clear_format',
                    icon: 'richeditor-format-clear-text',
                    shortcuts: normalizeKeyName('Ctrl') + ' + \\',
                },
                // specific extensions
                Dropcursor: {
                    extension: Dropcursor.configure({
                        color: '#4868FD',
                        width: 2,
                    }),
                },
                Gapcursor: {
                    extension: Gapcursor,
                },
                Mention: {
                    extension: Mention.configure({
                        suggestion: CustomSuggestion,
                        renderHTML: function ({ options, node }) {
                            return [
                                'span',
                                {
                                    class: 'mention',
                                    'data-id': node.attrs.id,
                                },
                                `${node.attrs.label}`,
                            ];
                        },
                    }),
                },
                // Manage options group "more"
                More: {
                    label: 'richtext.more_format_options',
                    icon: 'action',
                },
                // Manage options group "specific"
                Specific: {
                    label: 'richtext.more_insert_options',
                    icon: 'action',
                },
            },
            // Group options for the editor
            configOptions: {
                CustomTags: ['CustomTags', 'Image', 'Gapcursor', 'Dropcursor'],
                CustomTagsAndFields: ['CustomTagsAndFields', 'Gapcursor', 'Dropcursor'],
                CustomTagsAI: ['CustomTagsAI', 'Gapcursor', 'Dropcursor'],
                Heading: ['Heading', 'Blockquote', 'Code'],
                SellsyAI: ['SellsyAI', 'SellsyAIInput', 'BubbleMenuAI', 'Highlight'],
                Table: ['Table', 'TableCell', 'TableHeader', 'TableRow'],
            },
            localOptions: this.options,
        };
    },
    computed: {
        // get extensions TIPTAP for all options
        editorExtensions() {
            this.removeOptionWithoutFeatureFlagEnabled();
            const options = this.resolveAllOptions(this.localOptions);
            return (
                options
                    .map((option) => this.resolveExtension(option, options))
                    // Remove null values
                    .filter((extension) => extension !== null)
            );
        },
    },
    mounted() {
        if (this.useStorage) {
            this.enableStorage();
        }
        this.initializeEditor();
    },
    beforeUnmount() {
        if (this.editor) {
            this.editor.destroy();
        }
    },
    methods: {
        // get extension from extensionsMap for one option
        getExtensionOption(option) {
            return this.extensionsMap[option];
        },
        initializeEditor() {
            // tiptap use custom this
            const _self = this;
            // Default extensions for the editor
            const defaultExtensions = [
                Document,
                Text,
                TextStyle,
                Paragraph,
                ListItem,
                UndoRedo,
                HardBreak,
                HorizontalRule,
                Table,
                TableCell,
                TableHeader,
                TableRow,
                CustomSignature,
            ];
            // Init editor TIPTAP with options
            this.editor = new Editor({
                content: this.modelValue,
                editable: !this.disabled,
                extensions: new Set([
                    Placeholder.configure({
                        placeholder: this.getPlaceholder(),
                    }),
                    ...defaultExtensions,
                    ...this.editorExtensions,
                    Strike, // TODO: add logic on editorExtensions (More / Specific)
                ]),
                onUpdate: () => {
                    // Emit input event to update v-model
                    this.$emit('update:modelValue', this.editor.getHTML());
                    if (this.storage) {
                        this.setStorage(this.editor.getHTML());
                    }
                },
                onFocus() {
                    _self.isFocus = true;
                },
                onBlur() {
                    _self.isFocus = false;
                },
                editorProps: {
                    handlePaste: (view, event) => _self.handleHtmlPaste(event),
                    handleDOMEvents: {
                        keydown: (view, event) => {
                            if (event.key === 'Enter') {
                                if (_self.disableEnter) {
                                    event.preventDefault();
                                }
                            }
                        },
                    },
                },
            });
        },
        // When HTML source is pasted as plain text (e.g. copied from a code
        // editor), render it as real HTML instead of letting it become a code
        // block or escaped text. Rich HTML copied from a web page keeps the
        // default ProseMirror parsing.
        handleHtmlPaste(event) {
            const clipboard = event.clipboardData;
            if (!clipboard) {
                return false;
            }
            const text = clipboard.getData('text/plain');
            if (!this.looksLikeHtml(text)) {
                return false;
            }
            const hasRenderedHtml = !!clipboard.getData('text/html');
            const isFromCodeEditor = !!clipboard.getData('vscode-editor-data');
            // Genuine rich HTML (e.g. from a web page) is parsed natively.
            if (hasRenderedHtml && !isFromCodeEditor) {
                return false;
            }
            this.editor.commands.insertContent(text);
            return true;
        },
        looksLikeHtml(text) {
            const trimmed = (text || '').trim();
            return (
                trimmed.startsWith('<') && trimmed.endsWith('>') && /<[a-z][^>]*>/i.test(trimmed)
            );
        },
        // Need overwrite editor with prop to link RichText editor with RichTextMenu editor
        overwriteEditorWithProp(editorProp) {
            this.editor = editorProp;
        },
        enableStorage() {
            //We have to check if the refRichText is not empty because this component is used to all RichText even if we don't have to use storage
            if (this.refRichText && this.refRichText !== '') {
                this.storage = new SessionStorage('overview_', this.refRichText);
                const contentStored = this.getStorage();
                if (contentStored && contentStored !== '') {
                    this.$emit('update:modelValue', contentStored);
                }
            }
        },
        getStorage() {
            return this.storage === undefined ? null : this.storage.get();
        },
        removeAITags(html) {
            const tempHTML = document.createElement('div');
            tempHTML.innerHTML = html;

            // remove ai loading dialog
            if (tempHTML.querySelector('sellsyaidialog')) {
                tempHTML.remove(tempHTML.querySelector('sellsyaidialog'));
            }

            // remove ai input
            if (tempHTML.querySelector('sellsyaiinput')) {
                tempHTML.remove(tempHTML.querySelector('sellsyaiinput'));
            }

            // remove au highlight
            if (!!tempHTML.querySelectorAll('mark').length) {
                tempHTML.querySelectorAll('mark').forEach((mark) => {
                    mark.outerHTML = mark.innerHTML;
                });
            }

            return tempHTML.innerHTML;
        },
        setStorage(html) {
            return this.storage === undefined ? null : this.storage.set(this.removeAITags(html));
        },
        removeOptionWithoutFeatureFlagEnabled() {
            if (!Feature.isEnabled('sellsy-ai')) {
                // Remove SellsyAI if feature flag is not enabled
                this.localOptions = this.localOptions.filter((option) => option !== 'SellsyAI');
            }
        },
        getPlaceholder() {
            if (
                Feature.isEnabled('sellsy-ai') &&
                this.showAIComponents &&
                this.typeRichText === 'email'
            ) {
                return this.$t.__('email.EmailPanel.placeholder');
            }
            return this.placeholder;
        },
        resolveAllOptions(options) {
            const allOptions = [];
            for (const option of options) {
                if (this.configOptions.hasOwnProperty(option)) {
                    allOptions.push(...this.configOptions[option]);
                } else {
                    allOptions.push(option);
                }
            }
            return [...new Set(allOptions)];
        },
        isTextAlignOption(option) {
            return [
                'TextAlign',
                'TextAlignLeft',
                'TextAlignCenter',
                'TextAlignRight',
                'TextAlignJustify',
            ].includes(option);
        },
        resolveExtension(option, options) {
            const extensionInfo = this.getExtensionOption(option);
            if (!extensionInfo) {
                return null;
            }
            // Manage types for all textAlign options (TextAlign, TextAlignLeft, TextAlignCenter, TextAlignRight, TextAlignJustify)
            if (this.isTextAlignOption(option)) {
                const includesHeading =
                    options.includes('Heading') || options.includes('HeadingTiptap');
                const configuredExtension = extensionInfo.extension.configure({
                    types: includesHeading ? ['heading', 'paragraph'] : ['paragraph'],
                });
                return configuredExtension;
            }
            return extensionInfo.extension ?? null;
        },
    },
};
